/**
 * Landing status for one run/workstream: the small non-blocking chip (header, Now row, board)
 * and whether the Land button shows. Landing is async: while a landing for the current HEAD is
 * queued/running (or auto-land will pick it up) the chip replaces the button; Land comes back
 * for a new HEAD, or as "Retry land" after a failure or cancel.
 */
import type { Landing, Project } from './types.js'
import { effectivePolicy } from './work.js'

export type LandingPhase =
  | 'none'
  | 'auto-pending'
  | 'awaiting'
  | 'queued'
  | 'verifying'
  | 'landed'
  | 'blocked'
  | 'cancelled'
  | 'interrupted'

export interface LandingChip {
  label: string
  tone: 'run' | 'ok' | 'fail' | 'wait' | 'idle'
  title?: string
}

export interface LandingStatus {
  phase: LandingPhase
  chip?: LandingChip
  showLand: boolean
  landLabel: 'Land' | 'Retry land'
  /** The landing the chip describes (drill-in target). */
  landing?: Landing
}

export interface LandingStatusInput {
  /** Newest landing of this run's workstream (or run). */
  landing?: Landing
  /** The project lands automatically when a turn ends (autonomy `land-main: auto`). */
  autoLand: boolean
  /** Current branch HEAD (progress evidence), when known. */
  headSha?: string
  /** Commits on the branch not yet on the target, when known. */
  ahead?: number
  /** Verified landing from the run's own progress evidence. */
  landedSha?: string
  /** Hub run-row landing state (plaintext hub / mock) when no landing report exists. */
  legacy?: { state: 'ready' | 'running' | 'blocked' | 'landed'; sha?: string } | undefined
  now: number
}

/** Does this project land on finish? (`@viko/project-config` effective policy.) */
export function autoLandsOnFinish(project: Pick<Project, 'loop' | 'config'> | undefined): boolean {
  if (!project) return false
  return (
    effectivePolicy(
      {
        loopProfile: project.config?.loopProfile ?? project.loop,
        ...(project.config?.autonomy ? { autonomy: project.config.autonomy } : {}),
      },
      'land-main',
    ) === 'auto'
  )
}

export function elapsed(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000))
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m${String(s % 60).padStart(2, '0')}s`
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}m`
}

const short = (sha?: string | null) => (sha ? sha.slice(0, 7) : '')
const landedLabel = (sha?: string | null) =>
  sha ? `Landed ${short(sha)} · pushed` : 'Landed · pushed'

/** "verifying 2/3 · 1m20s" while the recipe runs, else the pipeline step. */
function runningLabel(l: Landing, now: number): string {
  const recipe = l.recipe ?? []
  const i = recipe.findIndex((r) => r.status === 'running')
  if (i >= 0) {
    const started = recipe[i]?.startedAt ?? l.updatedAt
    return `Landing… verifying ${i + 1}/${recipe.length} · ${elapsed(now - started)}`
  }
  const step = l.step
  if (step === 'push' || step === 'merge') return 'Landing… pushing'
  if (step === 'verify' || step === 'cleanup' || step === 'deploy') return 'Landing… verifying push'
  if (step === 'rebase' || step === 'fetch') return 'Landing… rebasing'
  return `Landing… ${elapsed(now - l.createdAt)}`
}

export function landingStatus(i: LandingStatusInput): LandingStatus {
  const l = i.landing
  const hasWork = (i.ahead ?? 0) > 0
  // A landing describes the current HEAD unless the branch moved on since.
  const current =
    !!l &&
    (!i.headSha ||
      !l.headSha ||
      l.headSha === i.headSha ||
      (l.state === 'landed' && (l.commit === i.headSha || !hasWork)))
  const fresh = (): LandingStatus => {
    if (i.legacy?.state === 'running')
      return {
        phase: 'verifying',
        chip: { label: 'Landing…', tone: 'run' },
        showLand: false,
        landLabel: 'Land',
      }
    if (
      i.legacy?.state === 'landed' &&
      !(i.legacy.sha && i.headSha && hasWork && i.legacy.sha !== i.headSha)
    )
      return {
        phase: 'landed',
        chip: {
          label: landedLabel(i.legacy.sha ?? i.landedSha),
          tone: 'ok',
        },
        showLand: false,
        landLabel: 'Land',
      }
    if (i.landedSha && !hasWork && (!i.headSha || i.headSha === i.landedSha || i.ahead === 0))
      return {
        phase: 'landed',
        chip: { label: `Landed ${short(i.landedSha)} · pushed`, tone: 'ok' },
        showLand: false,
        landLabel: 'Land',
      }
    if (i.autoLand && hasWork)
      return {
        phase: 'auto-pending',
        chip: {
          label: 'Lands when checks pass',
          tone: 'idle',
          title: 'Viko lands this branch when the agent’s turn ends and the recipe passes.',
        },
        showLand: false,
        landLabel: 'Land',
      }
    return { phase: 'none', showLand: true, landLabel: 'Land' }
  }
  if (!l || !current) return fresh()
  switch (l.state) {
    case 'queued':
      return {
        phase: 'queued',
        chip: { label: l.auto ? 'Auto-landing… queued' : 'Landing queued', tone: 'run' },
        showLand: false,
        landLabel: 'Land',
        landing: l,
      }
    case 'running':
      return {
        phase: 'verifying',
        chip: { label: runningLabel(l, i.now), tone: 'run' },
        showLand: false,
        landLabel: 'Land',
        landing: l,
      }
    case 'landed':
      return {
        phase: 'landed',
        chip: { label: `Landed ${short(l.commit ?? l.headSha)} · pushed`, tone: 'ok' },
        showLand: false,
        landLabel: 'Land',
        landing: l,
      }
    case 'failed': {
      const reason =
        l.reason ??
        l.failure?.message ??
        (l.error !== 'landing failed' ? l.error : null) ??
        'landing failed'
      return {
        phase: 'blocked',
        chip: { label: `Landing blocked: ${reason}`, tone: 'fail', title: reason },
        showLand: true,
        landLabel: 'Retry land',
        landing: l,
      }
    }
    case 'interrupted':
      return {
        phase: 'interrupted',
        chip: {
          label: i.autoLand
            ? 'Interrupted by daemon restart — retrying'
            : 'Interrupted by daemon restart',
          tone: 'wait',
          title: l.failure?.nextAction,
        },
        showLand: !i.autoLand,
        landLabel: 'Retry land',
        landing: l,
      }
    case 'cancelled':
      return {
        phase: 'cancelled',
        chip: { label: 'Landing cancelled', tone: 'idle' },
        showLand: true,
        landLabel: 'Retry land',
        landing: l,
      }
    case 'awaiting_approval':
      return {
        phase: 'awaiting',
        chip: { label: 'Ready to land · needs your tap', tone: 'wait' },
        showLand: true,
        landLabel: 'Land',
        landing: l,
      }
    default:
      return fresh()
  }
}

/** Newest landing for a run: by run id, else by its workstream. */
export function landingForRun(
  landings: readonly Landing[] | undefined,
  run: { runId: string; workstreamId?: string | undefined } | undefined,
): Landing | undefined {
  if (!landings?.length || !run) return undefined
  let best: Landing | undefined
  for (const l of landings) {
    const mine =
      l.runId === run.runId || (!!run.workstreamId && l.workstreamId === run.workstreamId)
    if (mine && (!best || l.updatedAt > best.updatedAt)) best = l
  }
  return best
}

/** Chips for a list of runs (Now rows, board cards) from one landings query. */
export function landingChips(
  runs: readonly {
    runId: string
    workstreamId?: string | undefined
    projectId?: string | undefined
    ahead?: number | undefined
  }[],
  landings: readonly Landing[] | undefined,
  projects: readonly Pick<Project, 'id' | 'loop' | 'config'>[] | undefined,
  now: number,
): Map<string, LandingChip> {
  const out = new Map<string, LandingChip>()
  for (const r of runs) {
    if (!r.workstreamId) continue
    const s = landingStatus({
      landing: landingForRun(landings, r),
      autoLand: autoLandsOnFinish(projects?.find((p) => p.id === r.projectId)),
      ...(r.ahead !== undefined ? { ahead: r.ahead } : {}),
      now,
    })
    if (s.chip) out.set(r.runId, s.chip)
  }
  return out
}
